import React, { useState } from "react";
import api, { formatINR } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Loader2, Search, Trash2, ShieldCheck, AlertTriangle, CheckCircle2, FileWarning } from "lucide-react";
import { toast } from "sonner";

/** Scan → review → confirm, for test orders and the accounts behind them.
 *  Nothing is deleted until rows are ticked and DELETE is typed, and the
 *  backend re-validates every id before touching anything. */
export const TestOrderCleanup = () => {
    const [patterns, setPatterns] = useState("test,qa,demo,dummy,razorpay-test");
    const [nameNeedle, setNameNeedle] = useState("test");
    const [scan, setScan] = useState(null);
    const [picked, setPicked] = useState({});
    const [alsoAccounts, setAlsoAccounts] = useState(true);
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const run = async () => {
        setBusy(true);
        setResult(null);
        try {
            const qs = new URLSearchParams({ patterns, name_contains: nameNeedle });
            const { data } = await api.get(`/admin/cleanup/test-orders?${qs}`);
            setScan(data);
            setPicked(Object.fromEntries(data.orders.map((o) => [o.id, true])));
            setConfirm("");
            toast.success(`${data.summary.matched} test order(s) found · ${formatINR(data.summary.total_value)}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Scan failed");
        } finally {
            setBusy(false);
        }
    };

    const chosen = Object.entries(picked).filter(([, v]) => v).map(([k]) => k);

    const destroy = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/cleanup/test-orders", {
                order_ids: chosen, confirm, delete_accounts: alsoAccounts,
            });
            setResult(data);
            toast.success(`${data.orders_deleted} order(s) and ${data.accounts_deleted} account(s) deleted`);
            await run();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Cleanup failed");
        } finally {
            setBusy(false);
        }
    };

    const selectedValue = (scan?.orders || [])
        .filter((o) => picked[o.id])
        .reduce((s, o) => s + o.amount, 0);

    return (
        <div className="space-y-4" data-testid="test-order-cleanup">
            <div className="bg-amber-50 border border-amber-300 p-4 text-xs text-amber-900 flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                    <strong>Review every row before confirming.</strong> Deleting an order removes it from your sales
                    figures, day book and P&amp;L. Linked invoices are <em>unlinked, never deleted</em>, so your
                    accounting trail survives — the invoice number is reported so you can void it yourself. Stock is not
                    added back.
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
                <div>
                    <div className="label-eyebrow text-stone-500 mb-1">Customer email contains</div>
                    <Input value={patterns} onChange={(e) => setPatterns(e.target.value)} className="rounded-none" data-testid="order-cleanup-patterns" />
                </div>
                <div>
                    <div className="label-eyebrow text-stone-500 mb-1">Shipping name contains</div>
                    <Input value={nameNeedle} onChange={(e) => setNameNeedle(e.target.value)} className="rounded-none" data-testid="order-cleanup-name" />
                </div>
            </div>

            <Button onClick={run} disabled={busy} variant="outline" className="rounded-none border-stone-400" data-testid="order-cleanup-scan-btn">
                {busy && !scan ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                Scan for test orders
            </Button>

            {result && (
                <div className="bg-emerald-50 border border-emerald-300 p-4 text-sm space-y-2" data-testid="order-cleanup-result">
                    <div className="flex items-center gap-2 font-medium text-emerald-900">
                        <CheckCircle2 className="w-4 h-4" />
                        Deleted {result.orders_deleted} order(s) and {result.accounts_deleted} account(s).
                    </div>
                    {result.detached_invoices?.length > 0 && (
                        <div className="text-xs text-amber-900 flex items-start gap-1.5">
                            <FileWarning className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            Invoices unlinked (still in your books — void them if needed): <strong>{result.detached_invoices.join(", ")}</strong>
                        </div>
                    )}
                    {result.kept_accounts?.length > 0 && (
                        <ul className="text-xs text-stone-700 list-disc pl-5">
                            {result.kept_accounts.map((k, i) => <li key={i}>Kept <strong>{k.email}</strong> — {k.reason}</li>)}
                        </ul>
                    )}
                </div>
            )}

            {scan && (
                <>
                    <div className="text-xs text-stone-600" data-testid="order-cleanup-summary">
                        {scan.summary.total_orders} orders scanned · <strong>{scan.summary.matched} look like test data</strong> ·
                        combined value {formatINR(scan.summary.total_value)} · {scan.summary.with_invoice} carry an invoice.
                    </div>

                    {scan.orders.length === 0 ? (
                        <div className="bg-white border border-stone-200 p-6 text-sm text-stone-500" data-testid="order-cleanup-none">
                            No test orders match these criteria. Your order book is clean.
                        </div>
                    ) : (
                        <div className="bg-white border border-stone-200 overflow-x-auto">
                            <div className="px-4 py-2 border-b border-stone-100 flex items-center gap-3 text-xs">
                                <button onClick={() => setPicked(Object.fromEntries(scan.orders.map((o) => [o.id, true])))} className="text-sky-700 hover:underline" data-testid="order-cleanup-select-all">Select all</button>
                                <button onClick={() => setPicked({})} className="text-stone-500 hover:underline" data-testid="order-cleanup-select-none">Clear</button>
                                <span className="ml-auto text-stone-500">{chosen.length} selected · {formatINR(selectedValue)}</span>
                            </div>
                            <table className="w-full text-sm" data-testid="order-cleanup-table">
                                <thead className="bg-stone-50 text-left text-xs uppercase tracking-widest text-stone-500">
                                    <tr>
                                        <th className="p-3 w-10"></th>
                                        <th className="p-3">Order</th>
                                        <th className="p-3">Customer</th>
                                        <th className="p-3 text-right">Amount</th>
                                        <th className="p-3">Date</th>
                                        <th className="p-3">Why it matched</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {scan.orders.map((o) => (
                                        <tr key={o.id} className="border-t border-stone-100" data-testid={`order-cleanup-row-${o.id}`}>
                                            <td className="p-3">
                                                <Checkbox
                                                    checked={!!picked[o.id]}
                                                    onCheckedChange={(v) => setPicked((p) => ({ ...p, [o.id]: !!v }))}
                                                    data-testid={`order-cleanup-check-${o.id}`}
                                                />
                                            </td>
                                            <td className="p-3">
                                                <div className="font-mono text-xs text-stone-900">#{o.reference}</div>
                                                <div className="text-[11px] text-stone-500">
                                                    {o.order_type} · {o.status}
                                                    {o.invoice_number && <span className="text-amber-700"> · invoice {o.invoice_number}</span>}
                                                </div>
                                            </td>
                                            <td className="p-3">
                                                <div className="text-stone-900">{o.customer_name}</div>
                                                <div className="font-mono text-[11px] text-stone-500">{o.email}</div>
                                            </td>
                                            <td className="p-3 text-right font-mono">{formatINR(o.amount)}</td>
                                            <td className="p-3 text-xs text-stone-500">{o.date ? new Date(o.date).toLocaleDateString() : "—"}</td>
                                            <td className="p-3 text-xs">
                                                {o.reasons.map((r) => (
                                                    <span key={r} className="inline-block mr-1 mb-1 px-1.5 py-0.5 border border-red-300 text-red-700 text-[10px]">{r}</span>
                                                ))}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {scan.linked_accounts.length > 0 && (
                        <div className="bg-white border border-stone-200" data-testid="order-cleanup-accounts">
                            <div className="px-4 py-2 border-b border-stone-100 text-xs uppercase tracking-widest text-stone-500">
                                Accounts behind these orders
                            </div>
                            <ul className="p-4 space-y-1.5 text-xs">
                                {scan.linked_accounts.map((a) => (
                                    <li key={a.id} className="flex items-center gap-2" data-testid={`order-cleanup-account-${a.id}`}>
                                        <span className={a.will_be_deleted ? "text-red-700" : "text-stone-500"}>
                                            {a.will_be_deleted ? "will be deleted" : "kept"}
                                        </span>
                                        <span className="font-mono text-stone-800">{a.email}</span>
                                        <span className="text-stone-400">
                                            {a.orders_in_selection} of {a.total_orders} order(s) in this list
                                            {a.appointments ? ` · ${a.appointments} appointment(s)` : ""}
                                            {!a.email_matches_test_pattern ? " · email isn't a test pattern" : ""}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <ul className="text-[11px] text-stone-500 list-disc pl-5 space-y-0.5">
                        {(scan.warnings || []).map((w) => <li key={w}>{w}</li>)}
                    </ul>

                    {chosen.length > 0 && (
                        <div className="bg-red-50 border border-red-300 p-4 space-y-3" data-testid="order-cleanup-confirm-box">
                            <div className="text-sm text-red-900">
                                This permanently deletes <strong>{chosen.length} order(s)</strong> worth{" "}
                                <strong>{formatINR(selectedValue)}</strong> from your sales history. It cannot be undone.
                            </div>
                            <label className="flex items-center gap-2 text-xs text-red-900">
                                <Checkbox checked={alsoAccounts} onCheckedChange={(v) => setAlsoAccounts(!!v)} data-testid="order-cleanup-also-accounts" />
                                Also delete the test customer accounts left with no orders
                            </label>
                            <div className="flex gap-2 items-end flex-wrap">
                                <div>
                                    <div className="label-eyebrow text-red-800 mb-1">Type DELETE to confirm</div>
                                    <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-none w-44 bg-white" placeholder="DELETE" data-testid="order-cleanup-confirm-input" />
                                </div>
                                <Button
                                    onClick={destroy}
                                    disabled={busy || confirm !== "DELETE"}
                                    className="rounded-none bg-red-700 hover:bg-red-800 text-white"
                                    data-testid="order-cleanup-delete-btn"
                                >
                                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                                    Delete {chosen.length} order(s)
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {!scan && (
                <div className="text-[11px] text-stone-500 flex items-start gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600" />
                    Scanning is read-only. You'll see every matching order with its ID, customer, amount and date before anything is removed.
                </div>
            )}
        </div>
    );
};
